import { html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { TeComponent } from '../common/te-component';
import { theme } from '../common/theme';
import { IconName } from '../te-icon';
import { TeList } from './te-list';
import { booleanConverter } from '../common/converters';
import '../te-checkbox';

/**
 * List Item Component
 *
 * @slot - Default slot for list item content
 */
@customElement('te-list-item')
export class TeListItem extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-list-item {
        display: flex;
        flex-direction: row;
        align-items: center;
        height: fit-content;
        padding-right: 16px;
        padding-left: 16px;
        min-height: 48px;
        overflow: hidden;
      }

      .te-list-item.selectable {
        cursor: pointer;
        gap: 16px;
      }

      .te-list-item.icon {
        padding-left: 0px;
      }

      .te-list-item:hover {
        background: ${theme.colors.shade1};
      }

      .te-list-item.checked {
        background: ${theme.colors.primary.v0}0D;
      }

      .te-list-text-wrapper {
        display: flex;
        flex: 1;
        flex-direction: column;
        justify-content: center;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      te-checkbox {
        pointer-events: none;
      }
    `,
  ];

  /** Icon name from icon pack */
  @property({ type: String }) icon?: IconName;
  /** Color for icon svg */
  @property({ type: String }) iconColor?: string;
  /** Primary text value */
  @property({ type: String }) text?: string;
  /** Color of primary text */
  @property({ type: String }) textColor?: string;
  /** Secondary text value */
  @property({ type: String }) subtext?: string;
  /** Color of secondary text */
  @property({ type: String }) subtextColor?: string;
  /** Checked status of checkbox inside state */
  @property({ type: Boolean, converter: booleanConverter }) checked?: boolean;

  @state() rowHeight = 48;

  getRowHeight() {
    return (this.parentElement as TeList).rowHeight;
  }

  setRowHeight(height: number) {
    this.rowHeight = height;
  }

  updated() {
    if (this.text && this.subtext) (this.parentElement as TeList).setRowHeight(56);
  }

  renderIcon() {
    if (!this.icon) return;
    return html`
      <te-icon
        color=${this.iconColor || theme.colors.shade5.toString()}
        size="16"
        wrapperSize=${this.getRowHeight()}
        name="${this.icon}"
      ></te-icon>
    `;
  }

  renderText() {
    const classes = ['te-list-text-wrapper'];
    if (this.text && this.subtext) classes.push('two-lines');
    return html`
      <div class="${classes.join(' ')}">
        <te-text
          display="block"
          size="14"
          weight="700"
          color=${this.textColor || theme.colors.text.toString()}
          truncate
        >
          ${this.text}
        </te-text>
        <te-spacing direction="y" size="2"></te-spacing>
        <te-text
          display="block"
          size="12"
          color=${this.subtextColor || this.textColor || theme.colors.text.toString()}
          truncate
        >
          ${this.subtext}
        </te-text>
      </div>
    `;
  }

  renderCheckbox() {
    if (this.checked === undefined) return;
    return html`
      <te-checkbox .checked=${this.checked}></te-checkbox>
    `;
  }

  render() {
    const classes = ['te-list-item'];
    if (this.checked !== undefined) classes.push('selectable');
    if (this.checked) classes.push('checked');
    if (this.icon) classes.push('icon');
    return html`
      <div
        style="height: ${this.rowHeight}px"
        class="${classes.join(' ')}"
        @click=${this.handleClick}
      >
        ${this.renderCheckbox()} ${this.renderIcon()} ${this.renderText()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-list-item': TeListItem;
  }
}
